import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, Pills } from "@/components/Bits";
import { RATES, getBoltDiameters, sizeToNumber } from "@/data/boltData";
import { NUT_RATES, getNutSizes } from "@/data/nutData";
import { fmtInt, fmtDia, fmtLen, fmtNutSize } from "@/lib/format";

const CATS = [
  { id: "in_full", label: "INCH FULL", kind: "bolt", system: "inches" },
  { id: "in_half", label: "INCH HALF", kind: "bolt", system: "inches" },
  { id: "m_full", label: "METRIC FULL", kind: "bolt", system: "metric" },
  { id: "m_half", label: "METRIC HALF", kind: "bolt", system: "metric" },
  { id: "bsw", label: "NUT BSW", kind: "nut" },
  { id: "old", label: "NUT OLD", kind: "nut" },
  { id: "new", label: "NUT NEW", kind: "nut" },
];

export function TablesPage() {
  const [cat, setCat] = useState("in_full");
  const [q, setQ] = useState("");
  const active = CATS.find((c) => c.id === cat);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto no-scrollbar">
        <Pills testid="tables-cat" value={cat} onChange={(v) => { setCat(v); setQ(""); }} size="sm" options={CATS.map((c) => ({ value: c.id, label: c.label }))} />
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input data-testid="tables-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search size…" className="h-11 rounded-xl border-slate-700 bg-slate-900/60 pl-10 text-white" />
      </div>

      <Card className="p-0 overflow-hidden">
        {active.kind === "bolt" ? <BoltMatrix typeId={cat} system={active.system} q={q} /> : <NutTable specId={cat} q={q} />}
      </Card>
      <p className="pb-2 text-center text-[11px] text-slate-600">Approx. count per 50 KG bag</p>
    </div>
  );
}

function BoltMatrix({ typeId, system, q }) {
  const table = RATES[typeId];
  const allDias = useMemo(() => getBoltDiameters(typeId), [typeId]);
  const allLens = useMemo(() => Object.keys(table).sort((a, b) => sizeToNumber(a) - sizeToNumber(b)), [table]);

  const ql = q.trim().toLowerCase();
  const dias = ql ? (allDias.filter((d) => fmtDia(system, d).toLowerCase().includes(ql)).length ? allDias.filter((d) => fmtDia(system, d).toLowerCase().includes(ql)) : allDias) : allDias;
  const lens = ql ? (allLens.filter((l) => fmtLen(system, l).toLowerCase().includes(ql)).length ? allLens.filter((l) => fmtLen(system, l).toLowerCase().includes(ql)) : allLens) : allLens;

  return (
    <div className="overflow-auto" style={{ maxHeight: "60vh" }}>
      <table className="w-full border-collapse text-right font-mono text-xs" data-testid="bolt-table">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-800 text-slate-300">
            <th className="sticky left-0 z-20 bg-slate-800 px-3 py-2 text-left">LEN \ DIA</th>
            {dias.map((d) => (
              <th key={d} className="whitespace-nowrap px-3 py-2 font-bold text-orange-300">{fmtDia(system, d)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lens.map((l, i) => (
            <tr key={l} className={i % 2 ? "bg-slate-900/40" : ""}>
              <td className="sticky left-0 z-10 whitespace-nowrap bg-slate-900 px-3 py-2 text-left font-bold text-cyan-400">{fmtLen(system, l)}</td>
              {dias.map((d) => (
                <td key={d} className="whitespace-nowrap px-3 py-2 text-slate-200">{table[l][d] != null ? fmtInt(table[l][d]) : <span className="text-slate-700">–</span>}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NutTable({ specId, q }) {
  const sizes = getNutSizes(specId);
  const ql = q.trim().toLowerCase();
  const rows = ql ? sizes.filter((s) => fmtNutSize(specId, s).toLowerCase().includes(ql)) : sizes;
  return (
    <div className="overflow-auto" style={{ maxHeight: "60vh" }}>
      <table className="w-full font-mono text-sm" data-testid="nut-table">
        <thead className="sticky top-0 bg-slate-800 text-slate-300">
          <tr>
            <th className="px-4 py-2.5 text-left">SIZE</th>
            <th className="px-4 py-2.5 text-right">COUNT / 50 KG</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={s} className={i % 2 ? "bg-slate-900/40" : ""}>
              <td className="px-4 py-2.5 font-bold text-cyan-400">{fmtNutSize(specId, s)}</td>
              <td className="px-4 py-2.5 text-right text-slate-100">{fmtInt(NUT_RATES[specId][s])}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={2} className="py-6 text-center text-slate-600">No match</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
